import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, newKey } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInHouse, useMenu, useMyShift, useOpenOrders, useRsMutation, type Bill, type ChosenModifier, type MenuItem, type Order } from '../api/restaurant';
import { formatDate, formatGhs } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PayDialog, VariantDialog } from './RestaurantOrder';
import { menuPhotoUrl } from '../ui/photo';

interface CartLine {
  key: string;
  itemId: string;
  name: string;
  variantId: string;
  variantName: string;
  unit: number;
  qty: number;
  note: string;
  modifierIds: string[];
  chosen: ChosenModifier[];
  station: 'KITCHEN' | 'BAR';
}

const blocked = (i: MenuItem) => i.soldOut || !i.variants.some((v) => v.inStock);

/**
 * A fast screen for the bar and for quick restaurant sales: categories always on show, big tiles, and the order beside
 * them. It is only a quicker way to use the same orders, bills, tills and stock as the table screens; nothing here is a
 * separate record.
 */
export function BarSale() {
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const report = useReport();
  const menu = useMenu();
  const shift = useMyShift(can('shift.manage'));
  const open = useOpenOrders();
  const [cat, setCat] = useState('');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [choose, setChoose] = useState<MenuItem | null>(null);
  const [who, setWho] = useState<'WALKIN' | 'TAB'>('WALKIN');
  const [tabName, setTabName] = useState('');
  const [tabOrder, setTabOrder] = useState<string | null>(null);
  const [roomOpen, setRoomOpen] = useState(false);
  const [paying, setPaying] = useState<{ bill: Bill; order: Order } | null>(null);
  const [busy, setBusy] = useState(false);

  const cats = menu.data ?? [];
  const active = cats.find((c) => c.id === cat) ?? cats[0];
  const q = search.trim().toLowerCase();
  const items = q ? cats.flatMap((c) => c.items).filter((i) => i.name.toLowerCase().includes(q)) : (active?.items ?? []);
  const total = cart.reduce((s, l) => s + l.unit * l.qty, 0);
  const count = cart.reduce((s, l) => s + l.qty, 0);
  const tabs = useMemo(() => (open.data ?? []).filter((o) => o.target === 'TAKEAWAY' && o.label), [open.data]);
  const tillOpen = !!shift.data;

  function add(item: MenuItem, variantId: string, qty: number, note: string, modifierIds: string[], chosen: ChosenModifier[]) {
    const v = item.variants.find((x) => x.id === variantId)!;
    const unit = v.price + chosen.reduce((s, m) => s + m.price, 0);
    setCart((cur) => {
      const same = cur.find((l) => l.variantId === variantId && l.note === note && l.modifierIds.join() === modifierIds.join());
      if (same) return cur.map((l) => (l === same ? { ...l, qty: Math.min(50, l.qty + qty) } : l));
      return [...cur, { key: newKey(), itemId: item.id, name: item.name, variantId, variantName: v.name, unit, qty, note, modifierIds, chosen, station: item.station }];
    });
  }
  const tap = (i: MenuItem) => {
    if (blocked(i)) return;
    if (i.variants.length !== 1 || i.modifierGroups.length > 0) return setChoose(i);
    add(i, i.variants[0]!.id, 1, '', [], []);
  };
  const bump = (key: string, d: number) => setCart((cur) => cur.flatMap((l) => (l.key !== key ? [l] : l.qty + d <= 0 ? [] : [{ ...l, qty: Math.min(50, l.qty + d) }])));
  const qtyOf = (i: MenuItem) => cart.filter((l) => l.itemId === i.id).reduce((s, l) => s + l.qty, 0);

  const place = useRsMutation(async (v: { target: 'TAKEAWAY' | 'ROOM_STAY'; bookingId?: string; label?: string; existing?: string | null }) => {
    let id = v.existing ?? null;
    const fresh = !id;
    try {
      if (!id) {
        const o = await api<Order>('/orders', { method: 'POST', body: { target: v.target, covers: 1, ...(v.bookingId ? { bookingId: v.bookingId } : {}), ...(v.label ? { label: v.label } : {}) } });
        id = o.id;
      }
      for (const l of cart) {
        await api(`/orders/${id}/lines`, { method: 'POST', body: { clientLineId: l.key, variantId: l.variantId, qty: l.qty, ...(l.note ? { note: l.note } : {}), ...(l.modifierIds.length ? { modifierIds: l.modifierIds } : {}) } });
      }
      await api(`/orders/${id}/send`, { method: 'POST', body: {} });
    } catch (e) {
      // nothing reached the bar yet, so do not leave an empty order lying about
      if (id && fresh) await api(`/orders/${id}/cancel`, { method: 'POST', body: { reason: 'Sale not completed' } }).catch(() => undefined);
      throw e;
    }
    return id;
  });

  const label = who === 'TAB' && !tabOrder ? tabName.trim() : undefined;
  const tabReady = who === 'WALKIN' || !!tabOrder || tabName.trim().length >= 2;
  const target = tabOrder ? tabs.find((t) => t.id === tabOrder)?.label : label;
  const reset = () => {
    setCart([]);
    setBusy(false);
    setTabName('');
    setTabOrder(null);
    setWho('WALKIN');
  };
  const orderArgs = { target: 'TAKEAWAY' as const, ...(label ? { label } : {}), existing: who === 'TAB' ? tabOrder : null };

  const sendOrder = () => {
    setBusy(true);
    place.mutate(orderArgs, {
      onError: (e) => { setBusy(false); report(e); },
      onSuccess: () => { toast.ok(who === 'TAB' ? `Sent to the bar. Kept open for ${target ?? 'the tab'}.` : 'Sent to the bar and kitchen'); reset(); },
    });
  };
  const quickOrder = () => {
    setBusy(true);
    place.mutate(orderArgs, {
      onError: (e) => { setBusy(false); report(e); },
      onSuccess: async (id) => {
        try {
          const bill = await api<Bill>(`/orders/${id}/bills`, { method: 'POST', body: {} });
          const order = await api<Order>(`/orders/${id}`);
          setPaying({ bill, order });
        } catch (e) {
          setBusy(false);
          report(e);
          toast.ok('The order was sent. Find it under Ongoing orders to bill it.');
          setCart([]);
        }
      },
    });
  };
  const chargeRoom = async (bookingId: string) => {
    setBusy(true);
    setRoomOpen(false);
    place.mutate(
      { target: 'ROOM_STAY', bookingId },
      {
        onError: (e) => { setBusy(false); report(e); },
        onSuccess: async (id) => {
          try {
            const bill = await api<Bill>(`/orders/${id}/bills`, { method: 'POST', body: {} });
            await api(`/bills/${bill.id}/pay`, { method: 'POST', body: { tender: 'ROOM', amount: bill.due, bookingId }, idempotencyKey: newKey() });
            toast.ok('Charged to the room and sent to the bar');
            reset();
          } catch (e) {
            setBusy(false);
            report(e);
            toast.ok('The order was sent but not charged. Find it under Ongoing orders.');
            setCart([]);
          }
        },
      },
    );
  };

  return (
    <div className="page bar-page">
      <h1 className="sr-only">Bar sale</h1>
      <nav className="bar-top no-print" aria-label="Shortcuts">
        <Link to="/restaurant" className="btn">Floor</Link>
        <span className="btn primary" aria-current="page">Bar sale</span>
        <Link to="/restaurant" className="btn">Ongoing{(open.data ?? []).length > 0 ? <Badge tone="warn">{(open.data ?? []).length}</Badge> : null}</Link>
        {can('kitchen.view') ? <Link to="/kitchen" className="btn">Bar and kitchen status</Link> : null}
        <span style={{ flex: 1 }} />
        {can('shift.manage') ? (
          <Link to="/till" className="btn" aria-label={tillOpen ? 'Till open' : 'Till closed'}>
            <Badge tone={tillOpen ? 'good' : 'warn'}>{tillOpen ? 'Till open' : 'Till closed'}</Badge>
          </Link>
        ) : null}
      </nav>

      <div className="bar-grid">
        <section className="card bar-cart" aria-label="This order">
          <div className="seg" role="group" aria-label="Who is it for">
            <button type="button" aria-pressed={who === 'WALKIN'} onClick={() => { setWho('WALKIN'); setTabOrder(null); }}>Walk-in</button>
            <button type="button" aria-pressed={who === 'TAB'} onClick={() => setWho('TAB')}>Open tab</button>
          </div>
          {who === 'TAB' ? (
            <div className="stack">
              {tabs.length > 0 ? (
                <div className="row" style={{ flexWrap: 'wrap' }} role="group" aria-label="Open tabs">
                  {tabs.map((t) => (
                    <button key={t.id} type="button" className="chip" aria-pressed={tabOrder === t.id} onClick={() => setTabOrder(tabOrder === t.id ? null : t.id)}>{t.label} · {formatGhs(t.total)}</button>
                  ))}
                </div>
              ) : null}
              {!tabOrder ? <input className="input" aria-label="Name for a new tab" placeholder="Name for a new tab" value={tabName} onChange={(e) => setTabName(e.target.value)} /> : null}
            </div>
          ) : null}

          {cart.length === 0 ? (
            <p className="small muted" style={{ padding: '24px 0' }}>Tap a drink or dish to add it.</p>
          ) : (
            <ul className="bar-lines" aria-label="Items in this order">
              {cart.map((l) => (
                <li key={l.key}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="title">{l.name}{l.variantName && l.variantName !== 'Regular' ? ` · ${l.variantName}` : ''}</div>
                    {l.chosen.length || l.note ? <div className="small muted">{[...l.chosen.map((m) => m.name), l.note].filter(Boolean).join(', ')}</div> : null}
                  </div>
                  <div className="row">
                    <Button aria-label={`One less ${l.name}`} onClick={() => bump(l.key, -1)}>−</Button>
                    <strong className="num" style={{ minWidth: 24, textAlign: 'center' }}>{l.qty}</strong>
                    <Button aria-label={`One more ${l.name}`} onClick={() => bump(l.key, 1)}>+</Button>
                  </div>
                  <span className="num" style={{ minWidth: 84, textAlign: 'right' }}>{formatGhs(l.unit * l.qty)}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="total-row"><span>Total{count > 0 ? ` · ${count} item${count === 1 ? '' : 's'}` : ''}</span><span className="amount num">{formatGhs(total)}</span></div>
          <p className="small muted">Prices include any taxes. The bill shows them broken down.</p>
          <div className="stack">
            {can('bill.settle') ? (
              <Button variant="primary" block disabled={cart.length === 0 || !tabReady || !tillOpen} loading={busy} onClick={quickOrder}>Quick order: send and pay</Button>
            ) : null}
            {can('bill.settle') && can('shift.manage') && !tillOpen && shift.isSuccess ? <p className="hint">Open your till to take payment. <Link to="/till">Go to the till</Link></p> : null}
            {can('bill.settle') ? <Button block disabled={cart.length === 0 || busy || who === 'TAB'} onClick={() => setRoomOpen(true)}>Charge to room</Button> : null}
            <Button block disabled={cart.length === 0 || !tabReady || busy} onClick={sendOrder}>{who === 'TAB' ? 'Place order: keep the tab open' : 'Place order: send to bar'}</Button>
            <Button block variant="ghost" disabled={cart.length === 0 || busy} onClick={() => setCart([])}>Clear</Button>
          </div>
        </section>

        <div className="bar-rail" role="group" aria-label="Category">
          {cats.map((c) => (
            <button key={c.id} type="button" className="bar-cat" aria-pressed={!q && (active?.id ?? '') === c.id} onClick={() => { setSearch(''); setCat(c.id); }}>{c.name}</button>
          ))}
        </div>

        <section className="bar-menu" aria-label="Menu">
          <input className="input" placeholder="Search the menu" aria-label="Search the menu" value={search} onChange={(e) => setSearch(e.target.value)} />
          {menu.isLoading ? (
            <Loading rows={3} />
          ) : menu.error ? (
            <ErrorNote error={menu.error} retry={() => void menu.refetch()} />
          ) : cats.length === 0 ? (
            <Empty icon="inbox" title="The menu is empty">A manager can add dishes under Menu and tables.</Empty>
          ) : (
            <div className="bar-tiles">
              {items.map((i) => {
                const n = qtyOf(i);
                const out = blocked(i);
                return (
                  <button key={i.id} type="button" className="bar-tile" disabled={out} onClick={() => tap(i)} aria-label={`${i.name}, ${out ? 'sold out' : formatGhs(Math.min(...i.variants.map((v) => v.price)))}`}>
                    {n > 0 ? <span className="bar-qty" aria-hidden="true">{n}</span> : null}
                    <span className="bar-photo" aria-hidden="true">{i.photoVersion ? <img src={menuPhotoUrl(i.id, i.photoVersion)} alt="" loading="lazy" /> : i.name.slice(0, 1).toUpperCase()}</span>
                    <span className="title">{i.name}</span>
                    <span className="small muted num">{out ? (i.soldOut ? 'Sold out' : 'Out of stock') : i.variants.length > 1 ? `from ${formatGhs(Math.min(...i.variants.map((v) => v.price)))}` : formatGhs(i.variants[0]?.price ?? 0)}</span>
                  </button>
                );
              })}
              {items.length === 0 ? <p className="small muted">Nothing here.</p> : null}
            </div>
          )}
        </section>
      </div>

      <VariantDialog item={choose} onClose={() => setChoose(null)} pending={false} onAdd={(variantId, qty, note, modifierIds, chosen) => { add(choose!, variantId, qty, note, modifierIds, chosen); setChoose(null); }} />
      {roomOpen ? <RoomDialog total={total} onClose={() => setRoomOpen(false)} onPick={(id) => void chargeRoom(id)} /> : null}
      {paying ? <PayDialog bill={paying.bill} order={paying.order} onClose={() => { setPaying(null); reset(); void nav('/bar', { replace: true }); }} /> : null}
    </div>
  );
}

/** Pick the in-house guest to charge: by room number, name or phone number. */
function RoomDialog({ total, onClose, onPick }: { total: number; onClose: () => void; onPick: (bookingId: string) => void }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const guests = useInHouse(search.trim(), true);
  const list = guests.data ?? [];
  const chosen = list.find((g) => g.bookingId === picked);
  return (
    <Modal open onClose={onClose} title="Charge to a room">
      <div className="stack">
        <p className="small muted">Only guests who are checked in are listed. The amount goes on their bill and is paid when they check out.</p>
        <Field id="rm-search" label="Room number, guest name or phone number">
          <input id="rm-search" className="input" value={search} onChange={(e) => { setSearch(e.target.value); setPicked(null); }} autoComplete="off" autoFocus />
        </Field>
        <div className="list" role="listbox" aria-label="Guests in the hotel">
          {guests.isLoading ? <Loading rows={2} /> : list.length === 0 ? <p className="small muted">No checked-in guest matches.</p> : null}
          {list.map((g) => (
            <button key={g.bookingId} type="button" role="option" aria-selected={picked === g.bookingId} className="item" style={{ textAlign: 'left', cursor: 'pointer', outline: picked === g.bookingId ? '2px solid var(--brand, #0e6b41)' : undefined }} onClick={() => setPicked(g.bookingId)}>
              <span className="title">Room {g.room ?? '?'} · {g.guest}</span>
              {g.checkOut ? <span className="small muted">leaves {formatDate(g.checkOut)}</span> : null}
            </button>
          ))}
        </div>
        <div className="total-row"><span>To charge</span><span className="amount num">{formatGhs(total)}</span></div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!chosen} onClick={() => chosen && onPick(chosen.bookingId)}>{chosen ? `Charge ${formatGhs(total)} to Room ${chosen.room ?? '?'}` : 'Choose a guest'}</Button>
        </div>
      </div>
    </Modal>
  );
}
